import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { bezugsSchluessel } from "@kundeninfo/api-client";
import type {
  ApiClient, Positionsbezug, Zahlplan as ZahlplanStand, ZahlplanRate, ZahlplanSetzen,
} from "@kundeninfo/api-client";
import type { KopffeldTabelle } from "./vorgangsdetail.js";
import { Auswahl, Eingabe, Hinweis, Optionen } from "@kundeninfo/ui/me-design";
import type { EingabeProps } from "@kundeninfo/ui/me-design";
import { KiKnopf, useBeimVerlassen, LoeschKnopf } from "./meHuellen.js";
import "./Zahlplan.css";

/**
 * DIE ZAHLUNGSVEREINBARUNG eines Belegs — EIN Baustein für den Kopfblock von Angebot
 * und Auftrag (Spec „Leistungszeiträume und Zahlplan am Auftrag", 14.09.2026,
 * „Oberfläche").
 *
 * **Er speichert NICHT selbst.** Er zeichnet und meldet seinen Entwurf nach oben; das
 * Speichern gehört dem Modal, in dem er steht — ein zweiter Speichern-Knopf im strikten
 * Panel wäre ein zweiter Ausgang, und die Hausregel kennt nur einen. Wörtlich die
 * Bauart von `BelegParteien.tsx`; die VERSION, mit der geschrieben wird, gehört dem
 * Modal (es bekommt sie beim Speichern des Kopfes frisch zurück).
 *
 * **Die Fachlichkeit steckt NICHT hier.** Welche Kombinationen gültig sind, welche
 * Mengenquelle angebunden ist, wie der Satz für den Beleg lautet — das entscheidet der
 * Server (`domain/billingPlanRules.ts`) und schickt es als `warnungen`, `vorlagen` und
 * `textzeilen` mit. `web` darf `domain` nicht einführen
 * (scripts/check-boundaries.mjs), und soll es auch nicht: Eine zweite, abgeschriebene
 * Regel wäre eine, die eines Tages anders urteilt.
 *
 * **`fehler` gegenüber `warnungen`.** Beides kommt vom Server, aber sie sagen
 * Verschiedenes: eine WARNUNG ist eine fachliche Auskunft zum gültigen, gespeicherten
 * Stand (z. B. R2 „Keine der zugeordneten Positionen hat einen Artikel mit
 * Abrechnungsdaten aus Monitor — die Rate kann keine Istmengen bekommen."; das frühere
 * Beispiel „kein Portal-Mandant hinterlegt" gibt es seit dem Paket „Abrechnungsquellen",
 * 29.09.2026, nicht mehr); ein FEHLER ist ein Befund über den
 * gespeicherten Stand selbst — etwa ein Positionsbezug, der ins Leere zeigt, weil die
 * Position inzwischen gelöscht wurde. Beide tragen einen Feldnamen mit Ratenindex
 * (`raten[2].itemIds`) und stehen deshalb an DERSELBEN Rate; Schlechtestes oben heisst
 * hier: Ein Fehler steht VOR einer Warnung an derselben Rate.
 *
 * **Was die Maske trotzdem wissen muss, ist eine ANZEIGEREGEL, keine zweite
 * Fachregel:** Quelle und Bemessungsstichtag gehören zur Bemessung nach ISTMENGE, Monat
 * und Tag zur Fälligkeit an einem FESTEN Tag. Würden sie immer stehen, böte die Maske
 * Felder an, die der Server beim Speichern wieder wegräumt
 * (`normalizeBillingPlanLine`) — und der Bearbeiter sähe seine Eingabe verschwinden.
 *
 * **Eigener CSS-Block `zpl__`** — er gehört allein dieser Datei und reist mit ihr an
 * alle Einbauorte.
 *
 * Seit Migration 029 sitzt er ausserdem an der ANGEBOTSVORLAGE
 * (`components/VorlageDetail.tsx`) — dem dritten Einbauort, und dem ersten ohne Kunden.
 * Er bekommt deshalb denselben `Positionsbezug` wie der Positions-Editor: Eine Vorlage
 * trägt beides, und zwei verschiedene Bezugstypen für dasselbe Objekt wären einer zu viel.
 *
 * **Er ist eine benannte GRUPPE** (`role="group"`, beschriftet von seiner eigenen
 * Überschrift): Ein Bildschirmleser soll beim Betreten hören, dass hier die
 * Zahlungsvereinbarung beginnt — dieselbe Bauart wie `BelegParteien.tsx`.
 */

export type ZahlplanClient = Pick<ApiClient, "getZahlplan">;
export type ZahlplanModalClient = ZahlplanClient & Pick<ApiClient, "setZahlplan">;

/** Eine Rate im Entwurf — genau der Rumpf von `setZahlplan`, ohne die Version. */
export type ZahlplanRateEntwurf = ZahlplanSetzen["raten"][number];
export type ZahlplanEntwurf = { raten: ZahlplanRateEntwurf[] };

export type ZahlplanProps = {
  client: ZahlplanClient;
  /**
   * WORAN der Zahlplan hängt — ein Vorgang eines Kunden oder eine Angebotsvorlage.
   *
   * Ein Wechsel setzt ALLES zurück und lädt neu; das gilt jetzt für den Bezug als GANZES.
   * Der Typ kommt aus dem Klienten (`Positionsbezug`) und ist hier nicht nachgebaut.
   */
  bezug: Positionsbezug;
  /** Das Präfix der Feld-Kennungen — dasselbe, mit dem die Maske ihre übrigen
   *  Kopffelder benennt (`kauf`, `kang`). Kein `useId()`: Ein Server-Befund soll den
   *  Kopfblock aufklappen und GENAU diesen Abschnitt zeigen. */
  idPraefix: string;
  /** Über diesem Abschnitt läuft ein Schreibvorgang — dann sind die Felder gesperrt. */
  verdeckt?: boolean | undefined;
  /** Zeichnet den Abschnitt LESEND: die fertigen Sätze des Servers, kein einziges
   *  Bedienelement. Das ist die Gestalt im Angebots-Lesedetail (Spec:
   *  „Angebotsdetail: Zahlungsvereinbarung lesend"). */
  nurLesend?: boolean | undefined;
  /** Die Feldbefunde des Servers zum letzten Speicherversuch (SERVERNAME → Satz), aus
   *  `ZahlplanGriff.befunde`. Die Namen tragen den Index der Rate
   *  (`raten[2].sharePercent`). */
  befunde?: Readonly<Record<string, string>> | undefined;
  /** Nach jeder EINGABE. Ohne Eingabe kommt nichts — ein ungerührter Abschnitt soll
   *  kein PUT auslösen. */
  onEntwurf?: ((entwurf: ZahlplanEntwurf) => void) | undefined;
};

/**
 * Die Feldnamen dieses Abschnitts für die Kopffaltung. Die SERVER-Namen tragen einen
 * Index (`raten[2].sharePercent`) und passen deshalb in keine feste Tabelle — der
 * Abschnitt hat statt dessen EINE Kennung, und `istZahlplanFeld` erkennt jeden Befund,
 * der zu ihm gehört.
 */
export const ZAHLPLAN_KOPFFELDER: KopffeldTabelle = { raten: "zahlplan" };

/** Gehört dieser Server-Feldname zum Zahlplan-Abschnitt? */
export function istZahlplanFeld(serverFeld: string): boolean {
  return serverFeld === "raten" || serverFeld.startsWith("raten[");
}

/** Die Vorgabewerte einer NEUEN Rate — dieselben wie die Spalten-Defaults der
 *  Migration 026, damit „Hinzufügen" und „nichts angeben" dasselbe ergeben. */
const NEUE_RATE: ZahlplanRateEntwurf = {
  id: null, label: null, sharePercent: 100, measurement: "PLANNED",
  quantitySource: null, measurementDay: null, measurementMonth: null,
  measurementDayOfMonth: null, cycle: "YEARLY", dueRule: "SUBPERIOD_START",
  dueMonth: null, dueDayOfMonth: null, dueOffsetDays: 0, parameters: null,
  itemIds: [],
};

const BEMESSUNG_TEXT: Record<string, string> = {
  PLANNED: "nach Planmenge", ACTUAL: "nach Istmenge",
};
const QUELLE_TEXT: Record<string, string> = {
  MANUAL: "von Hand", MONITOR: "Monitor (Bestände)", AIRCALL: "Aircall (Gesprächszeit)",
};
const STICHTAG_TEXT: Record<string, string> = {
  PERIOD_START: "Beginn des Leistungszeitraums", FIXED_DAY: "fester Tag im Jahr",
  DUE_DATE: "der Fälligkeitstag",
};
const RHYTHMUS_TEXT: Record<string, string> = {
  YEARLY: "jährlich", HALF_YEARLY: "halbjährlich", QUARTERLY: "quartalsweise",
  MONTHLY: "monatlich",
};
const FAELLIG_TEXT: Record<string, string> = {
  SUBPERIOD_START: "zu Beginn des Zeitraums", SUBPERIOD_END: "zum Ende des Zeitraums",
  FIXED_DAY: "an einem festen Tag",
};

/** Eine gespeicherte Rate als Entwurfszeile — die Kennung reist MIT (Festlegung Z6:
 *  Der Vollersatz respektiert die Identität der Zeile). */
function alsEntwurf(rate: ZahlplanRate): ZahlplanRateEntwurf {
  const { sortIndex: _sortIndex, ...rest } = rate;
  return rest;
}

/** Text → Zahl für die Zahlenfelder des Zahlplans: Komma oder Punkt, leer = 0 (wie früher
 *  das geleerte native Zahlenfeld); `null`, solange der Text noch keine Zahl ist („-“, „1e“). */
export function zplZahlAus(text: string): number | null {
  const roh = text.trim().replace(",", ".");
  if (roh === "") return 0;
  if (!/^-?(\d+\.?\d*|\.\d+)$/.test(roh)) return null;
  const zahl = Number(roh);
  return Number.isFinite(zahl) ? zahl : null;
}

/** Zahl → Text im Feld, deutsch mit Komma. */
function zplText(zahl: number): string {
  return String(zahl).replace(".", ",");
}

/**
 * Ein Zahlenfeld des Zahlplans (ME-Etappe 4 P5, P12 W1): Die Einheit steht im Feld
 * (Entscheid 39), der Wert geht als ZAHL in den Entwurf — ein geleertes Feld als 0, wie
 * vorher beim nativen Zahlenfeld. Ohne `beschriftung` trägt `name` den zugänglichen Namen
 * (Monat neben dem Tag), die leere Beschriftungszeile fällt dann weg.
 *
 * Das Feld führt TEXT (Muster `ZahlFeld`/`EinheitFeld` des Belegdesigners), nicht
 * `art="zahl"`: Ein `<input type="number">` meldet bei einem Zwischenstand wie „-“ oder
 * „33,“ den Wert "" — der Entwurf bekam 0, die kontrollierte Hülle schrieb „0“ zurück, und
 * aus „-14“ wurde „014“ (Gesamtreview W1). Jetzt bleibt der getippte Text stehen; in den
 * Entwurf geht er, sobald er eine Zahl ist. Beim Verlassen zeigt das Feld wieder den
 * Entwurfswert — ein Rest ohne Zahl („-“) fällt damit sichtbar weg.
 */
function ZplZahl(p: {
  id?: string | undefined; beschriftung?: string | undefined; name?: string | undefined;
  einheit?: string | undefined; hilfe?: string | undefined; fehler?: string | undefined;
  /** Tastatur am Telefon; fehlt sie, gibt es die volle Tastatur (der Versatz braucht „-“). */
  tastatur?: "decimal" | "numeric" | undefined;
  wert: number; gesperrt: boolean; onZahl: (zahl: number) => void;
}): ReactElement {
  const host = useRef<HTMLElement | null>(null);
  const [text, setText] = useState(zplText(p.wert));
  // Kommt der Wert von aussen (Vorlage, Neu laden), folgt das Feld; ein Zwischenstand wie
  // „33,“ bleibt stehen, solange er denselben Wert meint.
  useEffect(() => {
    setText((alt) => (zplZahlAus(alt) === p.wert ? alt : zplText(p.wert)));
  }, [p.wert]);
  const wertRef = useRef(p.wert);
  wertRef.current = p.wert;
  useBeimVerlassen(host, () => { setText(zplText(wertRef.current)); });
  const optional: Partial<EingabeProps> = {};
  if (p.id !== undefined) optional.id = p.id;
  if (p.beschriftung !== undefined) optional.beschriftung = p.beschriftung;
  if (p.name !== undefined) optional["aria-label"] = p.name;
  if (p.einheit !== undefined) optional.einheit = p.einheit;
  if (p.hilfe !== undefined) optional.hilfe = p.hilfe;
  if (p.tastatur !== undefined) optional.inputMode = p.tastatur;
  if (p.fehler !== undefined && p.fehler !== "") optional.fehler = p.fehler;
  if (p.gesperrt) optional.gesperrt = true;
  if (p.beschriftung === undefined) optional.className = "zpl__ohne-beschriftung";
  return (
    <Eingabe ref={host} wert={text} {...optional}
      onGeaendert={(d) => {
        if (p.gesperrt) return;
        setText(d.wert);
        const zahl = zplZahlAus(d.wert);
        if (zahl !== null) p.onZahl(zahl);
      }} />
  );
}

export function Zahlplan(props: ZahlplanProps): ReactElement {
  const { client, bezug, idPraefix, verdeckt = false,
          nurLesend = false, befunde = {}, onEntwurf } = props;
  /** Die Abhängigkeit ist der INHALT des Bezugs, nicht das Objekt: Das Literal im JSX ist
   *  bei jedem Zeichnen ein neues, und ein Effekt an ihm lüde nach jedem Zeichnen neu.
   *  Gebildet wird er beim TYP (`bezugsSchluessel` in @kundeninfo/api-client) und nicht
   *  hier — der Positions-Editor braucht genau denselben Schlüssel, und zwei Abschriften
   *  derselben Regel laufen beim nächsten Bezugsfall auseinander. */
  const schluessel = bezugsSchluessel(bezug);
  const [stand, setStand] = useState<ZahlplanStand | null>(null);
  const [raten, setRaten] = useState<ZahlplanRateEntwurf[]>([]);
  const [vorlageKey, setVorlageKey] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  /** Der Riegel gegen Antworten, die zu spät kommen (Muster `BelegParteien`). */
  const lauf = useRef(0);
  const getZahlplan = client.getZahlplan;
  /** Der aktuelle Bezug, ausserhalb der Abhängigkeitsliste erreichbar (Muster `bezugRef`
   *  im Positions-Editor): Gelesen wird er im Effekt, abgehängt ist er am Schlüssel. */
  const bezugRef = useRef(bezug);
  bezugRef.current = bezug;

  /** EIN Lade-Effekt, der beim Wechsel ALLES zurücksetzt: Kein UI-Zustand überlebt
   *  einen Kontextwechsel, und der Wechsel des Vorgangs ist hier der schärfste — den
   *  Entwurf des vorigen Belegs weiterzureichen hiesse, fremde Raten einzutragen. */
  useEffect(() => {
    const meiner = ++lauf.current;
    setStand(null);
    setRaten([]);
    setVorlageKey("");
    setFehler(null);
    getZahlplan(bezugRef.current)
      .then((antwort) => {
        if (lauf.current !== meiner) return;
        setStand(antwort);
        setRaten(antwort.raten.map(alsEntwurf));
      })
      .catch(() => {
        if (lauf.current !== meiner) return;
        setFehler("Die Zahlungsvereinbarung konnte nicht geladen werden.");
      });
    return () => { lauf.current++; };
  }, [getZahlplan, schluessel]);

  /** Übernehmen und nach oben melden. Die Meldung steht AUSSERHALB eines
   *  `setState`-Aktualisierers (Muster `BelegParteien`): Ein Aktualisierer muss frei von
   *  Nebenwirkungen sein — React ruft ihn unter StrictMode absichtlich doppelt auf. */
  function setze(neu: ZahlplanRateEntwurf[]): void {
    setRaten(neu);
    onEntwurf?.({ raten: neu });
  }

  function aendere(index: number, teil: Partial<ZahlplanRateEntwurf>): void {
    setze(raten.map((r, i) => (i === index ? { ...r, ...teil } : r)));
  }

  function verschiebe(index: number, richtung: -1 | 1): void {
    const ziel = index + richtung;
    if (ziel < 0 || ziel >= raten.length) return;
    const neu = [...raten];
    [neu[index], neu[ziel]] = [neu[ziel]!, neu[index]!];
    setze(neu);
  }

  function wendeVorlageAn(): void {
    const vorlage = stand?.vorlagen.find((v) => v.key === vorlageKey);
    if (vorlage === undefined) return;
    // Die Raten einer Vorlage sind NEU — sie tragen keine Kennung und ERSETZEN den
    // bisherigen Plan. Ein Anhängen ergäbe fast immer über 100 Prozent.
    //
    // `sortIndex` wird ABGESTREIFT: Der Schreibrumpf kennt ihn nicht (die Reihenfolge
    // der Liste IST die Ordnung, das Repository vergibt sie neu), und
    // `additionalProperties: false` wiese ihn mit 400 ab — an einem Feld, das die Maske
    // selbst mitgeschickt hätte.
    setze(vorlage.raten.map(({ sortIndex: _sortIndex, ...rest }) =>
      ({ ...rest, id: null })));
  }

  /**
   * LESEND ist dieser Abschnitt aus DREI Gründen: weil der Aufrufer es sagt
   * (`nurLesend`, das Angebots-Lesedetail), weil über ihm ein fremdes Panel liegt
   * (`verdeckt`) — oder weil der Beleg selbst nicht mehr schreibbar ist: Eine ABGELÖSTE
   * Angebotsversion weist `veralteteVersionRiegel` mit 422 ANGEBOT_VERALTET ab. Felder
   * anzubieten, deren Speichern der Server verweigert, hiesse, umsonst tippen zu lassen.
   */
  const gesperrt = verdeckt || nurLesend || stand?.veraltet === true;
  const feldId = (index: number, teil: string) =>
    `${idPraefix}-zahlplan-${index}-${teil}`;
  const befundFuer = (index: number, feld: string) =>
    befunde[`raten[${index}].${feld}`];
  /** Alle Warnungen dieser Rate — GENERISCH an der Rate gezeigt, nicht nur an einem
   *  einzelnen (vielleicht gar nicht eingeblendeten) Feld: Eine Warnung zu
   *  `quantitySource` bleibt gültig, auch wenn die Bemessung gerade auf PLANNED steht
   *  und das Feld deshalb gar nicht zu sehen ist. */
  const warnungenFuerRate = (index: number) =>
    stand?.warnungen.filter((w) => w.field.startsWith(`raten[${index}]`)) ?? [];
  /** Ein Fehler des GESPEICHERTEN Standes an dieser Rate (irgendein Feld) — Schlechtestes
   *  oben: Er steht vor der Warnung derselben Rate. Nicht zuordenbare Fehler (kein
   *  `raten[i]`-Präfix) zeigt der Kopf des Abschnitts. */
  const fehlerFuerRate = (index: number) =>
    stand?.fehler.filter((f) => f.field.startsWith(`raten[${index}]`)) ?? [];
  const kopfFehler = stand?.fehler.filter((f) => !istZahlplanFehlerMitIndex(f.field)) ?? [];

  if (fehler !== null) {
    return <Hinweis art="fehler">{fehler}</Hinweis>;
  }
  if (stand === null) {
    return <p className="zpl__laedt">Zahlungsvereinbarung wird geladen …</p>;
  }

  // LESEND: die fertigen Sätze des Servers, kein einziges Bedienelement.
  if (nurLesend || stand.veraltet) {
    return (
      <section className="zpl" role="group" aria-label="Zahlungsvereinbarung"
        id={`${idPraefix}-zahlplan`}>
        <h4 className="zpl__titel">Zahlungsvereinbarung</h4>
        {stand.veraltet && (
          <p className="zpl__hinweis" role="status">
            Diese Angebotsversion ist abgelöst — die Zahlungsvereinbarung ist nur noch
            lesbar. Die gültige Version trägt ihre eigene.
          </p>
        )}
        {stand.textzeilen.length === 0
          ? <p className="zpl__leer">Noch keine Zahlungsvereinbarung hinterlegt.</p>
          : (
            <ul className="zpl__saetze">
              {/* Der Schlüssel benennt die ZEILE (Platz + Satz), nicht den Inhalt: Zwei
                  gleich gebaute Raten ergeben denselben Satz, und zwei Kinder mit
                  demselben Schlüssel hängt React beim nächsten Zeichnen falsch um. */}
              {stand.textzeilen.map((satz, i) => <li key={`${i}-${satz}`}>{satz}</li>)}
            </ul>
          )}
      </section>
    );
  }

  return (
    <section className="zpl" role="group" aria-label="Zahlungsvereinbarung"
      id={`${idPraefix}-zahlplan`}>
      <h4 className="zpl__titel">Zahlungsvereinbarung</h4>
      <p className="zpl__hinweis">
        Die Beträge beziehen sich auf das LEISTUNGSJAHR: Der Einzelpreis einer Position
        ist ihr Jahrespreis, und der Rhythmus teilt ihn auf.
      </p>

      {/* Fehler, die keiner Rate zuzuordnen sind — Schlechtestes oben, vor der Liste. */}
      {kopfFehler.map((f) => (
        <Hinweis art="fehler" key={f.field}>{f.message}</Hinweis>
      ))}

      {raten.length === 0 && (
        <p className="zpl__leer">
          Noch keine Zahlungsvereinbarung — ohne sie entstehen keine geplanten
          Fälligkeiten.
        </p>
      )}

      {raten.map((rate, index) => {
        /*
         * Die Befunde dieser Rate — EINMAL geholt, weil sie zweimal gebraucht werden:
         * für die Absätze selbst und für die Kennungen, über die das `fieldset` sie als
         * seine Beschreibung ausweist (`aria-describedby`). Sie gelten der RATE und
         * nicht einem ihrer Felder (siehe `warnungenFuerRate`) — deshalb hängen sie an
         * der GRUPPE. Wer die Gruppe betritt, hört sie; die FELDbefunde (`befunde[…]`)
         * bleiben an ihrem jeweiligen Feld.
         */
        const fehlerDerRate = fehlerFuerRate(index);
        const warnungenDerRate = warnungenFuerRate(index);
        const befundKennungen = [
          ...fehlerDerRate.map((_f, i) => `${feldId(index, "fehler")}-${i}`),
          ...warnungenDerRate.map((_w, i) => `${feldId(index, "warnung")}-${i}`),
        ].join(" ");
        return (
          <fieldset className="zpl__rate" key={rate.id ?? `neu-${index}`}
            disabled={gesperrt}
            {...(befundKennungen === ""
              ? {} : { "aria-describedby": befundKennungen })}>
            <legend className="zpl__ratenTitel">Rate {index + 1}</legend>

            {/* Fehler des GESPEICHERTEN Standes an dieser Rate — vor jeder Warnung
                (Schlechtestes oben). Der Schlüssel trägt den PLATZ mit: Zwei Befunde
                zum selben Feld sind möglich (etwa zwei ungültige Positionsbezüge). */}
            {fehlerDerRate.map((f, i) => (
              <Hinweis art="fehler" key={`${f.field}-${i}`}
                id={`${feldId(index, "fehler")}-${i}`}>{f.message}</Hinweis>
            ))}
            {/* Warnungen des Servers an dieser Rate — generisch, unabhängig davon, ob
                das betroffene Feld gerade eingeblendet ist (siehe `warnungenFuerRate`). */}
            {warnungenDerRate.map((w, i) => (
              <Hinweis art="warnung" key={`${w.field}-${i}`}
                id={`${feldId(index, "warnung")}-${i}`}>{w.message}</Hinweis>
            ))}

          <div className="zpl__felder">
            <Eingabe id={feldId(index, "label")} beschriftung="Bezeichnung"
              wert={rate.label ?? ""} gesperrt={gesperrt}
              onGeaendert={(d) => aendere(index, { label: d.wert === "" ? null : d.wert })} />

            <ZplZahl id={feldId(index, "share")} beschriftung="Anteil" einheit="%" tastatur="decimal"
              wert={rate.sharePercent} gesperrt={gesperrt}
              fehler={befundFuer(index, "sharePercent")}
              onZahl={(zahl) => aendere(index, { sharePercent: zahl })} />

            <Auswahl id={feldId(index, "measurement")} beschriftung="Bemessung"
              wert={rate.measurement} gesperrt={gesperrt}
              onGeaendert={(d) => aendere(index, {
                measurement: d.wert as ZahlplanRateEntwurf["measurement"],
                // Zur Planmenge gehören weder Quelle noch Stichtag — dieselbe
                // Aufräumung, die der Server vornimmt (`normalizeBillingPlanLine`).
                ...(d.wert === "PLANNED"
                  ? { quantitySource: null, measurementDay: null,
                      measurementMonth: null, measurementDayOfMonth: null }
                  : { quantitySource: "MANUAL" as const,
                      measurementDay: "PERIOD_START" as const }),
              })}>
              {Object.entries(BEMESSUNG_TEXT).map(([wert, text]) => (
                <option key={wert} value={wert}>{text}</option>
              ))}
            </Auswahl>

            {rate.measurement === "ACTUAL" && (
              <>
                {/* Die Warnung zu diesem Feld steht generisch am Kopf der Rate (siehe
                    `warnungenFuerRate`) — auch dann, wenn dieses Feld (Bemessung
                    PLANNED) gar nicht eingeblendet ist. */}
                <Auswahl id={feldId(index, "quelle")} beschriftung="Mengenquelle"
                  wert={rate.quantitySource ?? "MANUAL"} gesperrt={gesperrt}
                  onGeaendert={(d) => aendere(index, {
                    quantitySource: d.wert as ZahlplanRateEntwurf["quantitySource"],
                  })}>
                  {Object.entries(QUELLE_TEXT).map(([wert, text]) => (
                    <option key={wert} value={wert}>{text}</option>
                  ))}
                </Auswahl>
                <Auswahl id={feldId(index, "stichtag")} beschriftung="Bemessungsstichtag"
                  wert={rate.measurementDay ?? "PERIOD_START"} gesperrt={gesperrt}
                  onGeaendert={(d) => aendere(index, {
                    measurementDay: d.wert as ZahlplanRateEntwurf["measurementDay"],
                    ...(d.wert === "FIXED_DAY"
                      ? { measurementMonth: 1, measurementDayOfMonth: 1 }
                      : { measurementMonth: null, measurementDayOfMonth: null }),
                  })}>
                  {Object.entries(STICHTAG_TEXT).map(([wert, text]) => (
                    <option key={wert} value={wert}>{text}</option>
                  ))}
                </Auswahl>
                {rate.measurementDay === "FIXED_DAY" && (
                  <div className="zpl__tag">
                    <ZplZahl id={feldId(index, "mtag")} beschriftung="Stichtag (Tag / Monat)" tastatur="numeric"
                      wert={rate.measurementDayOfMonth ?? 1} gesperrt={gesperrt}
                      onZahl={(zahl) => aendere(index, { measurementDayOfMonth: zahl })} />
                    <ZplZahl name="Stichtagsmonat" tastatur="numeric" wert={rate.measurementMonth ?? 1}
                      gesperrt={gesperrt}
                      onZahl={(zahl) => aendere(index, { measurementMonth: zahl })} />
                  </div>
                )}
              </>
            )}

            <Auswahl id={feldId(index, "cycle")} beschriftung="Rhythmus" wert={rate.cycle}
              gesperrt={gesperrt}
              onGeaendert={(d) => aendere(index, {
                cycle: d.wert as ZahlplanRateEntwurf["cycle"],
                // Der MONAT gehört nur zur jährlichen Zahlung an einem festen Tag.
                ...(d.wert === "YEARLY" ? {} : { dueMonth: null }),
              })}>
              {Object.entries(RHYTHMUS_TEXT).map(([wert, text]) => (
                <option key={wert} value={wert}>{text}</option>
              ))}
            </Auswahl>

            <Auswahl id={feldId(index, "due")} beschriftung="Fälligkeit" wert={rate.dueRule}
              gesperrt={gesperrt}
              onGeaendert={(d) => aendere(index, {
                dueRule: d.wert as ZahlplanRateEntwurf["dueRule"],
                ...(d.wert === "FIXED_DAY"
                  ? { dueDayOfMonth: 1, dueMonth: rate.cycle === "YEARLY" ? 1 : null }
                  : { dueDayOfMonth: null, dueMonth: null }),
              })}>
              {Object.entries(FAELLIG_TEXT).map(([wert, text]) => (
                <option key={wert} value={wert}>{text}</option>
              ))}
            </Auswahl>

            {rate.dueRule === "FIXED_DAY" && (
              <div className="zpl__tag">
                <ZplZahl id={feldId(index, "dtag")} tastatur="numeric"
                  beschriftung={rate.cycle === "YEARLY" ? "Fällig am (Tag / Monat)"
                    : "Fällig am (Tag im Teilzeitraum)"}
                  wert={rate.dueDayOfMonth ?? 1} gesperrt={gesperrt}
                  onZahl={(zahl) => aendere(index, { dueDayOfMonth: zahl })} />
                {rate.cycle === "YEARLY" && (
                  <ZplZahl name="Fälligkeitsmonat" tastatur="numeric" wert={rate.dueMonth ?? 1}
                    gesperrt={gesperrt}
                    onZahl={(zahl) => aendere(index, { dueMonth: zahl })} />
                )}
              </div>
            )}

            <ZplZahl id={feldId(index, "offset")} beschriftung="Versatz" einheit="Tage"
              hilfe="Negativ heisst Vorkasse." wert={rate.dueOffsetDays} gesperrt={gesperrt}
              onZahl={(zahl) => aendere(index, { dueOffsetDays: zahl })} />
          </div>

          {/* Mehrfachwahl der Positionen als `Optionen mehrfach` (me-optionen, Entscheid 40,
              ME-Etappe 4 P17; vorher natives Kästchen-`fieldset`, Übergang P5-3). Das
              `fieldset disabled` der Rate sperrt sie mit (gesperrt, Fokus bleibt). Die
              Positions-Ids sind UUIDs — ohne Komma. */}
          <Optionen beschriftung="Positionen (leer = alle)" mehrfach richtung="waagerecht"
            wert={rate.itemIds}
            onGeaendert={(d) => aendere(index, { itemIds: d.werte ?? [] })}>
            {stand.positionen.map((position) => (
              <option key={position.id} value={position.id}>{position.name}</option>
            ))}
          </Optionen>

          <div className="zpl__ratenAktionen">
            <KiKnopf art="leise" name={`Rate ${index + 1} nach oben`}
              deaktiviert={gesperrt || index === 0}
              onClick={() => verschiebe(index, -1)}>↑</KiKnopf>
            <KiKnopf art="leise" name={`Rate ${index + 1} nach unten`}
              deaktiviert={gesperrt || index === raten.length - 1}
              onClick={() => verschiebe(index, 1)}>↓</KiKnopf>
            {/* Leiser Papierkorb (Entscheid 43), nicht Gefahr: Die Rate fällt nur aus dem
                ENTWURF — „Abbrechen“ der Maske bringt sie zurück. */}
            <LoeschKnopf name={`Rate ${index + 1} entfernen`} deaktiviert={gesperrt}
              onClick={() => setze(raten.filter((_, i) => i !== index))} />
          </div>
        </fieldset>
        );
      })}

      <div className="zpl__aktionen">
        <KiKnopf deaktiviert={gesperrt} onClick={() => setze([...raten, { ...NEUE_RATE }])}>
          Rate hinzufügen
        </KiKnopf>
        <Auswahl id={`${idPraefix}-zahlplan-vorlage`} beschriftung="Vorlage"
          className="zpl__vorlage" wert={vorlageKey} gesperrt={gesperrt}
          onGeaendert={(d) => setVorlageKey(d.wert)}>
          <option value="">— wählen —</option>
          {stand.vorlagen.map((v) => (
            <option key={v.key} value={v.key}>{v.name}</option>
          ))}
        </Auswahl>
        {/* „Zahlungsvorlage“, nicht „Vorlage“ (Entscheid 98, ME-Etappe 4 P8): In der
            Angebotsmaske steht am Positionsblock ein zweites „Vorlage anwenden“ (Positions-
            vorlage) — zwei gleichnamige Knöpfe in einer Maske hört eine Vorlesestimme als
            denselben. */}
        <KiKnopf deaktiviert={gesperrt || vorlageKey === ""} onClick={wendeVorlageAn}>
          Zahlungsvorlage anwenden
        </KiKnopf>
      </div>

      {stand.textzeilen.length > 0 && (
        <div className="zpl__vorschau">
          <h5 className="zpl__vorschauTitel">So steht es auf dem Beleg</h5>
          {/* Der GESPEICHERTE Stand, nicht der Entwurf: Der Satz entsteht in der
              Domäne (`zahlplanTextzeilen`), und `web` darf sie nicht einführen. Er
              zieht beim nächsten Speichern nach — das sagt die Zeile darunter. */}
          <ul className="zpl__saetze">
            {stand.textzeilen.map((satz, i) => <li key={`${i}-${satz}`}>{satz}</li>)}
          </ul>
          <p className="zpl__vorschauHinweis">
            Stand der zuletzt gespeicherten Vereinbarung.
          </p>
        </div>
      )}
    </section>
  );
}

/** Trägt dieser Feldname einen Ratenindex (`raten[2].…`)? Nur SOLCHE Fehler stehen an
 *  einer Rate; alles andere (heute: nichts Bekanntes, aber die Antwort ist offen für mehr)
 *  zeigt der Kopf des Abschnitts. */
function istZahlplanFehlerMitIndex(feld: string): boolean {
  return /^raten\[\d+\]/.test(feld);
}
